import { addDaysLocal, todayLocal } from '@fos/shared/time';
import { Calendar, Mic, Plus } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { errorMessage, validationIssues } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { TimeInput } from '../../../ui/DateTimeInput';
import { Input } from '../../../ui/Input';
import { Mono } from '../../../ui/Mono';
import { toast } from '../../../ui/Toast';
import { showWarnings, useInvalidateTime } from '../hooks/useTimeMutations';
import { comboKey, splitComboKey, useCombos, useTimeOptions } from '../hooks/useTimeOptions';
import { chipDay, clockOrDash, previewTiming } from '../time-format';
import { LinkPicker } from './LinkPicker';
import { SentenceEntry } from './SentenceEntry';

/**
 * Pasek dodawania czasu (Figma 06 Czas / Wpisy): „Nad czym pracowałeś?”, klient · projekt (kropka koloru),
 * data („Dziś”), od, do, wyliczony czas (mono), „Dodaj czas”. Enter dodaje wpis. Pod spodem podpowiedź
 * o wpisie zdaniem albo głosem (przełącza pasek na pole zdania z podglądem). Po zapisie początek = koniec
 * poprzedniego wpisu (jak w Clockify), klient i data zostają.
 */

type BarField = 'description' | 'combo' | 'date' | 'start' | 'end' | 'endDate';

const FIELD_OF: Record<string, BarField> = {
  description: 'description',
  clientId: 'combo',
  projectId: 'combo',
  serviceId: 'combo',
  taskId: 'combo',
  date: 'date',
  start: 'start',
  end: 'end',
  durationSeconds: 'end',
  endDate: 'endDate',
};

interface BarValues {
  description: string;
  combo: string;
  date: string;
  start: string;
  end: string;
  endDate: string | null;
}

/** Data jako „Dziś” / „Wczoraj” / „pt 2.10” z natywnym kalendarzem (dobry także na mobile). */
function DateChip({ value, onChange, today }: { value: string; onChange: (v: string) => void; today: string }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={() => {
          const input = ref.current;
          if (!input) return;
          try {
            input.showPicker();
          } catch {
            input.focus();
          }
        }}
        aria-label={`Data: ${chipDay(value, today)}. Zmień datę`}
        className="inline-flex h-8 items-center gap-2 rounded-md px-2.5 text-body-s text-fg transition-colors hover:bg-surface focus-visible:outline-2 focus-visible:outline-accent"
      >
        <Calendar size={16} strokeWidth={1.75} aria-hidden className="text-fg-muted" />
        {chipDay(value, today)}
      </button>
      <input
        ref={ref}
        type="date"
        lang="pl-PL"
        tabIndex={-1}
        aria-hidden
        value={value}
        onChange={(e) => e.target.value && onChange(e.target.value)}
        className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
      />
    </span>
  );
}

export function AddTimeBar({ className }: { className?: string }) {
  const today = todayLocal();
  const options = useTimeOptions();
  const combos = useCombos(options.data);
  const create = trpc.time.create.useMutation();
  const invalidate = useInvalidateTime();
  const [sentence, setSentence] = useState(false);
  const [values, setValues] = useState<BarValues>({
    description: '',
    combo: comboKey(null, null),
    date: today,
    start: '',
    end: '',
    endDate: null,
  });
  const [errors, setErrors] = useState<Partial<Record<BarField, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const comboTouched = useRef(false);
  const descriptionRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const endDateButtonRef = useRef<HTMLButtonElement>(null);
  // po nieudanym zapisie fokus wraca do formularza (submit zdejmuje go z pola godziny): pierwsze błędne pole,
  // przy przejściu przez północ — „Koniec następnego dnia”, przy błędzie bez pola — opis (ponowienie Enterem)
  const [failedAttempt, setFailedAttempt] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reagujemy tylko na kolejne nieudane próby
  useEffect(() => {
    if (failedAttempt === 0) return;
    if (errors.endDate && endDateButtonRef.current) {
      endDateButtonRef.current.focus();
      return;
    }
    const invalidField = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
    (invalidField ?? descriptionRef.current)?.focus();
  }, [failedAttempt]);

  // domyślnie ostatnio używane połączenie klient · projekt
  useEffect(() => {
    const recent = options.data?.recent[0];
    if (recent && !comboTouched.current) {
      setValues((v) => ({ ...v, combo: comboKey(recent.clientId, recent.projectId) }));
    }
  }, [options.data]);

  const set = <K extends keyof BarValues>(key: K, value: BarValues[K], clear: BarField[] = [key as BarField]) => {
    setValues((v) => ({ ...v, [key]: value }));
    setErrors((e) => {
      const out = { ...e };
      for (const f of clear) delete out[f];
      return out;
    });
    setFormError(null);
  };

  const timing = previewTiming(values);

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    // pole godziny normalizuje wartość przy opuszczeniu: zapisujemy po jego onBlur
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.tagName === 'INPUT') active.blur();
    await new Promise((r) => setTimeout(r, 0));
    const v = valuesRef.current;
    const t = previewTiming(v);
    const local: Partial<Record<BarField, string>> = {};
    if (!v.start) local.start = 'Podaj początek';
    if (!v.end) local.end = 'Podaj koniec';
    if (Object.keys(local).length === 0 && t.error) {
      local[t.needsEndDate ? 'endDate' : (FIELD_OF[t.errorPath ?? 'end'] ?? 'end')] = t.needsEndDate
        ? 'Wpis przez północ: wskaż dzień zakończenia'
        : t.error;
    }
    if (Object.keys(local).length > 0) {
      setErrors(local);
      setFormError(Object.values(local)[0] ?? null);
      setFailedAttempt((n) => n + 1);
      return;
    }
    const links = splitComboKey(v.combo);
    try {
      const result = await create.mutateAsync({
        description: v.description.trim(),
        clientId: links.clientId,
        projectId: links.projectId,
        date: v.date,
        start: v.start,
        end: v.end,
        endDate: v.endDate,
      });
      await invalidate();
      showWarnings(result.warnings);
      if (result.warnings.length === 0) {
        toast.success('Dodano czas', { description: result.entry.description || undefined });
      }
      setValues((prev) => ({
        ...prev,
        description: '',
        start: v.endDate ? '' : v.end,
        end: '',
        endDate: null,
      }));
      setErrors({});
      setFormError(null);
      descriptionRef.current?.focus();
    } catch (err) {
      const fieldErrors: Partial<Record<BarField, string>> = {};
      for (const issue of validationIssues(err)) {
        const key = FIELD_OF[issue.path.split('.')[0] ?? ''];
        if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      setFormError(Object.values(fieldErrors)[0] ?? errorMessage(err, 'Nie udało się dodać czasu.'));
      setFailedAttempt((n) => n + 1);
    }
  };

  if (sentence) {
    return (
      <div className={cn('rounded-xl border border-line bg-panel p-4', className)}>
        <SentenceEntry
          onSaved={() => setSentence(false)}
          onCancel={() => setSentence(false)}
          onManual={(preview) => {
            if (preview) {
              comboTouched.current = Boolean(preview.client);
              setValues((v) => ({
                ...v,
                description: preview.description || v.description,
                combo: preview.client ? comboKey(preview.client.id, preview.project?.id ?? null) : v.combo,
                date: preview.date,
                start: preview.start ?? v.start,
                end: preview.end ?? v.end,
                endDate: preview.endDate,
              }));
            }
            setSentence(false);
          }}
        />
      </div>
    );
  }

  const invalid = (f: BarField) => (errors[f] ? true : undefined);

  return (
    <div className={className}>
      <form
        ref={formRef}
        onSubmit={(e) => void submit(e)}
        noValidate
        aria-label="Dodaj czas"
        className="flex min-h-14 items-center gap-1 rounded-xl border border-line bg-canvas py-2 pr-2 pl-2 transition-colors focus-within:border-line-strong"
      >
        <Input
          ref={descriptionRef}
          aria-label="Nad czym pracowałeś?"
          aria-invalid={invalid('description')}
          value={values.description}
          onChange={(e) => set('description', e.target.value)}
          placeholder="Nad czym pracowałeś?"
          maxLength={500}
          className="h-10 min-w-0 flex-1 border-transparent shadow-none hover:border-transparent focus:border-transparent focus:shadow-none"
        />
        <span aria-hidden className="h-6 w-px shrink-0 bg-line" />
        <LinkPicker
          bare
          combos={combos}
          value={values.combo}
          onValueChange={(key) => {
            comboTouched.current = true;
            set('combo', key);
          }}
          loading={options.isPending}
          className="shrink"
        />
        <span aria-hidden className="h-6 w-px shrink-0 bg-line" />
        <DateChip value={values.date} today={today} onChange={(v) => set('date', v, ['date', 'end', 'endDate'])} />
        <span aria-hidden className="h-6 w-px shrink-0 bg-line" />
        <span className="flex shrink-0 items-center gap-1.5 px-1">
          <TimeInput
            aria-label="Od"
            aria-invalid={invalid('start')}
            value={values.start}
            onValueChange={(v) => set('start', v, ['start', 'end', 'endDate'])}
            placeholder="08:30"
            className="h-8 w-[60px] border-transparent bg-elevated px-1 hover:border-transparent"
          />
          <span aria-hidden className="text-fg-muted">
            –
          </span>
          <TimeInput
            aria-label="Do"
            aria-invalid={invalid('end') ?? invalid('endDate')}
            value={values.end}
            onValueChange={(v) => set('end', v, ['end', 'endDate'])}
            placeholder="16:30"
            className="h-8 w-[60px] border-transparent bg-elevated px-1 hover:border-transparent"
          />
        </span>
        <span aria-hidden className="h-6 w-px shrink-0 bg-line" />
        <Mono
          aria-label={timing.seconds !== null ? `Czas ${clockOrDash(timing.seconds)}` : undefined}
          className={cn('w-14 shrink-0 text-center text-body-s', timing.seconds !== null ? 'text-fg' : 'text-fg-muted')}
        >
          {clockOrDash(timing.seconds)}
        </Mono>
        <Button type="submit" variant="primary" icon={Plus} loading={create.isPending}>
          Dodaj czas
        </Button>
      </form>
      <div className="mt-2 flex min-h-5 flex-wrap items-center gap-x-3 gap-y-2 px-4">
        {timing.needsEndDate && !values.endDate ? (
          <span role="alert" className="flex flex-wrap items-center gap-2 text-caption text-warning">
            Wpis przez północ: wskaż dzień zakończenia.
            <Button
              ref={endDateButtonRef}
              size="s"
              variant="secondary"
              onClick={() => set('endDate', addDaysLocal(values.date, 1), ['end', 'endDate'])}
            >
              Koniec następnego dnia
            </Button>
          </span>
        ) : values.endDate ? (
          <span className="flex items-center gap-2 text-caption text-fg-muted">
            Koniec następnego dnia ({chipDay(values.endDate, today)}).
            <button
              type="button"
              className="rounded-xs text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
              onClick={() => set('endDate', null, ['end', 'endDate'])}
            >
              Ten sam dzień
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setSentence(true)}
            className="inline-flex items-center gap-1.5 rounded-xs text-caption text-fg-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
          >
            <Mic size={14} strokeWidth={1.75} aria-hidden />
            Możesz też wpisać zdaniem albo głosem, np. „HelpMyCar 8:30–16:30 frontend”
          </button>
        )}
      </div>
      {formError && (
        <div className="mt-2">
          <FormMessage>{formError}</FormMessage>
        </div>
      )}
    </div>
  );
}
